import { useMemo } from 'react';
import { UseMapDataProps } from '../types';
import {
  geojsonToLeafletCoordinates,
  calculateCenterFromGeoJSON,
  GERMANY_CENTER,
} from '../../../utils/geometry';

/**
 * Hook für die Daten-Verarbeitung der Map-Komponente
 * Berechnet Koordinaten und Center
 */
export const useMapData = ({
  projectName,
  coordinates,
  geometrie,
  anlagen = [],
}: UseMapDataProps) => {

  // Konvertiere GeoJSON-Geometrie zu Leaflet-Format
  const flaecheCoordinates = useMemo((): [number, number][] => {
    return geojsonToLeafletCoordinates(geometrie);
  }, [geometrie]);

  // Extrahiere Koordinaten aus den Anlagen-Objekten (künftig über eigene Domäne)
  const anlagenCoordinates = useMemo((): [number, number][] => {
    const items = anlagen as Array<{ breitengrad?: number; laengengrad?: number }>;
    return items
      .filter(a => a.breitengrad != null && a.laengengrad != null)
      .map(a => [a.breitengrad!, a.laengengrad!] as [number, number]);
  }, [anlagen]);

  // Berechne automatisch das Zentrum aus dem Polygon oder den Anlagen
  const calculateCenter = (polygon: [number, number][]): [number, number] => {
    if (polygon.length === 0) {
      return coordinates || GERMANY_CENTER;
    }

    const latSum = polygon.reduce((sum, point) => sum + point[0], 0);
    const lngSum = polygon.reduce((sum, point) => sum + point[1], 0);

    return [latSum / polygon.length, lngSum / polygon.length];
  };

  // Berechne das Map-Center
  const mapCenter = useMemo((): [number, number] => {
    // Versuche zuerst GeoJSON für Center-Berechnung
    const geojsonCenter = geometrie
      ? calculateCenterFromGeoJSON(geometrie)
      : undefined;
    if (geojsonCenter) {
      return geojsonCenter;
    }

    if (flaecheCoordinates.length > 0) {
      return calculateCenter(flaecheCoordinates);
    } else if (anlagenCoordinates.length > 0) {
      return calculateCenter(anlagenCoordinates);
    } else {
      return coordinates || GERMANY_CENTER;
    }
  }, [geometrie, flaecheCoordinates, anlagenCoordinates, coordinates]);

  return {
    flaecheCoordinates,
    anlagenCoordinates,
    mapCenter,
  };
};
